import classNames from "classnames";
import { Fragment, PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UseListFiltersReturn } from "../../hooks/useListFilters";
import useOnClickOutside from "../../hooks/useOnclickOutside";
import { useI18n } from "../../hooks/useTranslations";
import { CustomError } from "../../http/errors";
import SvgArrowDropDown from "../icons/ArrowDropDown";
import SvgNavigateBefore from "../icons/NavigateBefore";
import SvgNavigateNext from "../icons/NavigateNext";
import Button from "./Button";
import ErrorText from "./ErrorText";
import ListItemMenu, { Action } from "./ListItemMenu";
import LoadingSpinner from "./LoadingSpinner";
import SearchBar from "./SearchBar";
import styles from "./Table.module.css";

interface Props<T> {
  className?: string;
  filters?: UseListFiltersReturn;
  searchOptions?: {
    disabled?: boolean;
    placeholder?: string;
    onChange: () => void;
  };
  total?: number;
  head?: ReactNode;
  onRowClick?: (item: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  items?: T[];
  renderRow: (item: T) => ReactNode;
  rowActions?: Action<T>[];
  footer?: ReactNode;
  isTall?: boolean;
  buttons?: {
    main: ReactNode | ReactNode[];
  };
  error?: CustomError;
  views?: {
    title: string;
    items: {
      label: string;
      url: string;
    }[];
  };
  secondaryButton?: ReactNode;
  innerTable?: (item: T, index: number) => ReactNode;
}

const Table = <T,>({
  className,
  filters,
  searchOptions,
  total = -1,
  head,
  items,
  renderRow,
  rowActions,
  footer,
  isTall = false,
  buttons,
  error,
  views,
  onRowClick,
  secondaryButton,
  innerTable,
}: Props<T>) => {
  const i18n = useI18n();
  // const permissions = usePermissionsContext();

  const isSearchable = filters && filters.isSearchable;
  const page = filters?.state.page ?? 1;
  const limit = filters?.state.limit ?? 5;

  const fromItem = (page - 1) * limit + 1;
  const maxToItem = fromItem + limit - 1;

  const [data, setData] = useState<{ items: T[]; total: number }>();

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const [showViewsMenu, setShowViewsMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, () => setShowViewsMenu(false));

  useEffect(() => {
    !!items && setData({ items, total });
  }, [items]);

  const toItem = Math.max(Math.min(maxToItem, data?.total ?? 0), 0);

  return (
    <>
      {(isSearchable || buttons) && (
        <div className={styles.heading}>
          {buttons?.main
            ? (Array.isArray(buttons.main) ? buttons.main : [buttons.main]).map((x, i) => (
                <Fragment key={`button_${i}`}>{x}</Fragment>
              ))
            : undefined}
          {/* <div className={styles.searchWrapper}> */}
          {isSearchable && (
            <SearchBar
              className={styles.search}
              placeholder={searchOptions?.placeholder ?? i18n.t.common.search}
              value={filters.state.q ?? ""}
              onChange={(term) => {
                if (searchOptions && !searchOptions?.disabled) {
                  searchOptions.onChange();
                  filters.setSearchTerm(term);
                }
              }}
            />
          )}
          {/* </div> */}
          {!!views && (
            <div className={styles.viewsContainer} ref={menuRef}>
              <Button buttonProps={{ onClick: () => setShowViewsMenu(true) }} glyph={SvgArrowDropDown}>
                {views.title}
              </Button>
              {showViewsMenu && (
                <div className={styles.views}>
                  {views.items
                    // .sort((x, y) => x.label.localeCompare(y.label, permissions.preferredLanguage ?? "en-UK"))
                    .map((x, index) => (
                      <Link key={`v_${index}`} to={x.url} onClick={() => setShowViewsMenu(false)}>
                        <div>{x.label}</div>
                      </Link>
                    ))}
                </div>
              )}
            </div>
          )}
          {!!secondaryButton && <div>{secondaryButton}</div>}
        </div>
      )}

      <table
        className={classNames(className, styles.table, {
          [styles.tall]: isTall,
        })}
      >
        {head && <thead>{head}</thead>}
        {!error && !!data?.items && (
          <>
            <tbody>
              {data?.items?.map((item, index) => {
                const isExpanded = expandedRows.has(index);

                return (
                  <Fragment key={`table_row_${index}`}>
                    <tr
                      onClick={
                        onRowClick || innerTable
                          ? (event) => {
                              onRowClick?.(item, event);
                              innerTable && toggleRow(index);
                            }
                          : undefined
                      }
                      className={styles.clickableRow}
                    >
                      {/* Optional icon column for expand/collapse arrow */}
                      {innerTable && (
                        <td className={styles.iconTd}>
                          <div className={classNames(styles.arrowIcon, { [styles.expanded]: isExpanded })}>
                            <SvgArrowDropDown />
                          </div>
                        </td>
                      )}

                      {renderRow(item)}

                      {rowActions?.length && (
                        <td width={1}>
                          <ListItemMenu item={item} actions={rowActions} />
                        </td>
                      )}
                    </tr>

                    {innerTable && isExpanded && (
                      <tr>
                        <td colSpan={999} className={styles.expandedRow}>
                          <div className={styles["inner-table"]}>{innerTable(item, index)}</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>

            {footer && <tfoot>{footer}</tfoot>}
          </>
        )}
      </table>

      {!error && !!data?.items && data.total === 0 && (
        <div className={styles.noItemsFound}>{i18n.t.common.noItemsFound}</div>
      )}

      {!error && !data?.items && (
        <div className={styles.loadingSpinner}>
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className={styles.errorLoadingData}>
          <ErrorText text={i18n.t.common.errorLoadingData} />
        </div>
      )}

      {!error && !!data?.items && data.total > 0 && (
        <div className={styles.controls}>
          <div>
            {fromItem} - {toItem} of {data.total}
          </div>
          {data.total > limit && (
            <div className={styles.pagination}>
              <Button
                glyph={SvgNavigateBefore}
                buttonProps={{
                  title: i18n.t.common.previousNRows(limit),
                  disabled: page === 1,
                  onClick: () => filters?.prevPage(),
                  className: styles.paginationButton,
                }}
              />
              <Button
                glyph={SvgNavigateNext}
                buttonProps={{
                  title: i18n.t.common.nextNRows(limit),
                  disabled: toItem === data.total,
                  onClick: () => filters?.nextPage(),
                  className: styles.paginationButton,
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

const IconTd = ({ children }: PropsWithChildren) => (
  <td className={styles.iconTd}>
    <div>{children}</div>
  </td>
);

Table.IconTd = IconTd;

export default Table;
